<template>
  <main class="home-sky">
    <div class="hud">
      <span class="live"><i></i> SYS.ONLINE</span>
      <span>{{ clock }}</span>
      <span>{{ isWalletConnected ? 'WALLET.LINKED' : 'WALLET.OFF' }}</span>
    </div>

    <section class="hero panel">
      <div class="copy">
        <p class="code">PLENTY // VERTICAL FARM OS</p>
        <h1>{{ $t('home.heroHeadline') }}</h1>
        <p class="lead">{{ $t('home.heroLead') }}</p>
        <div class="actions">
          <router-link to="/wealth" class="btn solid">{{ $t('home.ctaWealth') }}</router-link>
          <button v-if="!isWalletConnected" type="button" class="btn line" @click="handleStartJoining">
            {{ $t('home.startJoining') }}
          </button>
          <button type="button" class="btn line" @click="openOfficialSite">{{ $t('home.tradingBotsLink') }}</button>
        </div>
      </div>
    </section>

    <section class="metrics">
      <article v-for="item in growStats" :key="item.title" class="panel meter">
        <div class="meter-top">
          <small>{{ item.tag }}</small>
          <b>{{ item.value }}</b>
        </div>
        <h3>{{ item.title }}</h3>
        <div class="bar"><i :style="{ width: item.bar + '%' }"></i></div>
        <p>{{ item.desc }}</p>
      </article>
    </section>

    <section class="block">
      <header class="section-head">
        <span>PROT</span>
        <div>
          <p class="eyebrow">{{ $t('home.futureKicker') }}</p>
          <h2>{{ $t('home.futureTitle') }}</h2>
        </div>
      </header>
      <div class="pillars">
        <article v-for="(item, index) in pillars" :key="item.title" class="panel">
          <em>{{ String(index + 1).padStart(2, '0') }}</em>
          <h3>{{ item.title }}</h3>
          <p>{{ item.desc }}</p>
        </article>
      </div>
    </section>

    <section class="quote panel">
      <header class="section-head">
        <span>LOG</span>
        <div>
          <h2>{{ $t('home.whoTitle') }}</h2>
        </div>
      </header>
      <p class="who">{{ $t('home.whoCopy') }}</p>
      <pre>{{ $t('home.founderQuote') }}
— {{ $t('home.founderName') }}</pre>
    </section>

    <section class="block">
      <header class="section-head">
        <span>NODE</span>
        <div>
          <p class="eyebrow">{{ $t('home.farmsKicker') }}</p>
          <h2>{{ $t('home.farmsTitle') }}</h2>
        </div>
      </header>
      <div class="farms">
        <article v-for="(farm, index) in farms" :key="farm.place" class="panel">
          <div class="node-top">
            <span>{{ farm.place }}</span>
            <strong>N{{ String(index + 1).padStart(2, '0') }} · LIVE</strong>
          </div>
          <h3>{{ farm.title }}</h3>
          <p>{{ farm.desc }}</p>
          <div class="signal"><i></i><i></i><i></i><i></i></div>
        </article>
      </div>
    </section>

    <section class="block">
      <header class="section-head">
        <span>UNIT</span>
        <div>
          <p class="eyebrow">{{ $t('home.modulesKicker') }}</p>
          <h2>{{ $t('home.modulesTitle') }}</h2>
        </div>
      </header>
      <div class="modules">
        <button
          v-for="(item, index) in modules"
          :key="item.code"
          type="button"
          class="panel"
          @click="$router.push(`/wealth/product/${item.code}`)"
        >
          <div class="mod-top">
            <em>{{ item.code.toUpperCase() }}</em>
            <i class="led"></i>
          </div>
          <strong>{{ String(index + 1).padStart(2, '0') }}</strong>
          <span>{{ item.name }}</span>
        </button>
      </div>
    </section>

    <section class="faq">
      <header class="section-head">
        <span>FAQ</span>
        <div>
          <h2>{{ $t('home.faqTitle') }}</h2>
        </div>
      </header>
      <div class="faq-list panel">
        <button
          v-for="(item, index) in faqList"
          :key="item.question"
          type="button"
          class="faq-row"
          @click="toggleFaq(index)"
        >
          <em>Q{{ String(index + 1).padStart(2, '0') }}</em>
          <strong>{{ item.question }}</strong>
          <span>{{ item.isOpen ? '–' : '+' }}</span>
          <p v-if="item.isOpen">{{ item.answer }}</p>
        </button>
      </div>
    </section>
  </main>
</template>

<script>
import { ref, onMounted, onUnmounted, onActivated, onDeactivated, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import userStore from '@/utils/userStore'
import { wealthApi } from '@/services/api'
import { localizedProductName } from '@/utils/productLabels'
import { requestWalletAuthorization } from '@/utils/walletSession'
import { getDisplayTimezone } from '@/utils/timezone'

export default {
  name: 'Home',
  setup() {
    const { t, locale } = useI18n()
    const isWalletConnected = ref(false)
    const officialWebsiteUrl = ref('https://www.plenty.ag/')
    const faqOpenStates = ref([false, false, false])
    const clock = ref('--:--:--')
    let clockTimer = null

    const tickClock = () => {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: getDisplayTimezone(),
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).formatToParts(new Date())
      const get = (type) => parts.find((p) => p.type === type)?.value || '00'
      clock.value = `${get('hour')}:${get('minute')}:${get('second')}`
    }

    const checkWalletConnection = () => {
      isWalletConnected.value = userStore.isLoggedIn()
    }

    const handleStartJoining = () => {
      requestWalletAuthorization()
    }

    const openOfficialSite = () => {
      window.open(officialWebsiteUrl.value || 'https://www.plenty.ag/', '_blank')
    }

    const growStats = computed(() => ([
      { tag: 'YIELD.RATE', value: '86x', bar: 86, title: t('home.statYieldTitle'), desc: t('home.statYieldDesc') },
      { tag: 'LAND.USE', value: '1%', bar: 99, title: t('home.statLandTitle'), desc: t('home.statLandDesc') },
      { tag: 'H2O.SAVE', value: t('home.statWaterValue'), bar: 92, title: t('home.statWaterTitle'), desc: t('home.statWaterDesc') }
    ]))

    const pillars = computed(() => ([
      { title: t('home.pillar1Title'), desc: t('home.pillar1Desc') },
      { title: t('home.pillar2Title'), desc: t('home.pillar2Desc') },
      { title: t('home.pillar3Title'), desc: t('home.pillar3Desc') }
    ]))

    const farms = computed(() => ([
      { place: t('home.farm1Place'), title: t('home.farm1Title'), desc: t('home.farm1Desc') },
      { place: t('home.farm2Place'), title: t('home.farm2Title'), desc: t('home.farm2Desc') },
      { place: t('home.farm3Place'), title: t('home.farm3Title'), desc: t('home.farm3Desc') }
    ]))

    const catalog = ref([])

    const modules = computed(() => {
      if (catalog.value.length) {
        return catalog.value.map((item) => ({
          code: item.code,
          name: localizedProductName(item, locale.value)
        }))
      }
      return [
        { code: 'vertical', name: t('computing.垂直种植系统') },
        { code: 'hydroponics', name: t('computing.无土栽培') },
        { code: 'led', name: t('computing.LED人工光') },
        { code: 'ai_env', name: t('computing.AI环境控制') },
        { code: 'irrigation', name: t('computing.精准灌溉/营养液') },
        { code: 'sensors', name: t('computing.传感器+数据系统') },
        { code: 'automation', name: t('computing.自动化生产') },
        { code: 'breeding', name: t('computing.植物算法/育种') }
      ]
    })

    const faqList = computed(() => ([
      { question: t('home.faq1Question'), answer: t('home.faq1Answer'), isOpen: faqOpenStates.value[0] },
      { question: t('home.faq2Question'), answer: t('home.faq2Answer'), isOpen: faqOpenStates.value[1] },
      { question: t('home.faq3Question'), answer: t('home.faq3Answer'), isOpen: faqOpenStates.value[2] }
    ]))

    const toggleFaq = (index) => {
      faqOpenStates.value[index] = !faqOpenStates.value[index]
    }

    onMounted(() => {
      checkWalletConnection()
      wealthApi.listProducts().then((res) => {
        if (res.success) catalog.value = res.data || []
      }).catch(() => {})
      tickClock()
      clockTimer = setInterval(tickClock, 1000)
      window.addEventListener('user-connected', checkWalletConnection)
    })

    onActivated(() => {
      checkWalletConnection()
      tickClock()
      if (!clockTimer) clockTimer = setInterval(tickClock, 1000)
    })

    onDeactivated(() => {
      if (clockTimer) {
        clearInterval(clockTimer)
        clockTimer = null
      }
    })

    onUnmounted(() => {
      if (clockTimer) clearInterval(clockTimer)
      window.removeEventListener('user-connected', checkWalletConnection)
    })

    return {
      isWalletConnected,
      clock,
      handleStartJoining,
      openOfficialSite,
      growStats,
      pillars,
      farms,
      modules,
      faqList,
      toggleFaq
    }
  }
}
</script>

<style scoped>
.home-sky {
  min-height: 100vh;
  padding: 72px 12px 124px;
  background: #e7f6f8;
  color: #06122a;
}

.panel {
  background: #fff;
  border: 0;
  border-radius: 12px;
}

.hud {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding: 8px 10px;
  background: #2eb4d4;
  color: #fff;
  font-size: 11px;
  letter-spacing: 0.06em;
  font-weight: 600;
  text-transform: uppercase;
}

.hud span:last-child { text-align: right; }

.live {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.live i,
.led {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #7cffb2;
  box-shadow: 0 0 8px #7cffb2;
  animation: pulse 1.4s ease-in-out infinite;
}

.code {
  margin: 0 0 10px;
  color: #2eb4d4;
  font-size: 11px;
  letter-spacing: 0.08em;
  font-weight: 600;
  text-transform: uppercase;
}

.hero { padding: 20px 16px 18px; }

h1 {
  margin: 0;
  font-size: 34px;
  line-height: 1.08;
  letter-spacing: -0.04em;
  color: #06122a;
}

.lead {
  margin: 12px 0 0;
  color: #3d4f6b;
  line-height: 1.7;
  font-size: 14px;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 20px;
}

.btn {
  min-height: 42px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  font-family: var(--font-ui);
}

.btn.solid {
  background: #2eb4d4;
  color: #fff;
  border: 0;
  box-shadow: 0 0 18px rgba(0, 102, 255, 0.35);
}

.btn.line {
  background: #d7eef3;
  color: #2eb4d4;
  border: 0;
}

.metrics {
  display: grid;
  gap: 8px;
  margin-top: 10px;
}

.meter { padding: 14px 12px 12px; }

.meter-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.meter-top small,
.pillars em,
.node-top strong,
.mod-top em,
.faq-row em {
  color: #2eb4d4;
  font-size: 11px;
  font-weight: 600;
  font-style: normal;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.meter-top b {
  font-size: 28px;
  color: #2eb4d4;
  font-weight: 700;
  letter-spacing: -0.03em;
}

.bar {
  height: 4px;
  margin: 10px 0 8px;
  background: #d7eef3;
  border-radius: 99px;
}

.bar i {
  display: block;
  height: 100%;
  background: #2eb4d4;
  border-radius: 99px;
}

.section-head {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-bottom: 10px;
}

.section-head > span {
  min-width: 46px;
  padding: 4px 6px;
  background: #2eb4d4;
  color: #fff;
  font-size: 10px;
  letter-spacing: 0.08em;
  font-weight: 700;
}

.eyebrow { margin: 0 0 2px; color: #2eb4d4; font-size: 12px; }
h2, h3 { margin: 0; }
h2 { font-size: 20px; }
h3 { margin-top: 8px; font-size: 15px; }

.who, .pillars p, .farms p, .meter p, .faq-row p {
  margin: 8px 0 0;
  color: #3d4f6b;
  line-height: 1.6;
  font-size: 13px;
}

.block, .quote, .faq { margin-top: 28px; }
.pillars, .farms { display: grid; gap: 8px; }
.pillars article, .farms article, .quote { padding: 14px 12px; }

.node-top, .mod-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.signal {
  display: flex;
  gap: 3px;
  align-items: flex-end;
  height: 14px;
  margin-top: 12px;
}

.signal i {
  width: 4px;
  background: #2eb4d4;
}

.signal i:nth-child(1) { height: 4px; }
.signal i:nth-child(2) { height: 7px; }
.signal i:nth-child(3) { height: 10px; }
.signal i:nth-child(4) { height: 14px; }

.modules {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.modules button {
  min-height: 108px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  color: #06122a;
  text-align: left;
  cursor: pointer;
}

.modules strong {
  font-size: 22px;
  color: #2eb4d4;
  font-weight: 700;
  letter-spacing: -0.02em;
}

pre {
  margin: 12px 0 0;
  padding: 12px;
  background: #06122a;
  color: #7cffb2;
  font-size: 12px;
  line-height: 1.65;
  white-space: pre-wrap;
  font-family: var(--font-mono);
}

.faq-row {
  width: 100%;
  padding: 12px;
  display: grid;
  grid-template-columns: 42px 1fr auto;
  gap: 8px;
  text-align: left;
  color: inherit;
  background: transparent;
  border: 0;
  border-bottom: 0;
  background: #fff;
  cursor: pointer;
}

.faq-row p { grid-column: 1 / -1; }
.faq-list { overflow: hidden; }
.faq-row + .faq-row { margin-top: 2px; }

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}

</style>
